import React, { useState } from 'react';

interface Props {
  onAccept: () => void;
  onDecline: () => void;
}

export const TradeWithMeDisclaimer: React.FC<Props> = ({ onAccept, onDecline }) => {
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [checked, setChecked] = useState(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
      setScrolledToBottom(true);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.85)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem',
    }}>
      <div style={{
        background: '#111827', border: '1px solid #374151', borderRadius: 12,
        maxWidth: 640, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '1.5rem 1.5rem 0', borderBottom: '1px solid #374151' }}>
          <h2 style={{ margin: '0 0 0.5rem', color: '#f9fafb', fontSize: 20, fontWeight: 700 }}>
            Learn to Trade — Risk Disclaimer
          </h2>
          <p style={{ color: '#d97706', fontSize: 13, margin: '0 0 1rem', fontWeight: 600 }}>
            Please read the following carefully before accessing trade education content.
          </p>
        </div>

        {/* Scrollable content */}
        <div
          onScroll={handleScroll}
          style={{
            padding: '1.25rem 1.5rem', overflowY: 'auto', flex: 1,
            color: '#d1d5db', fontSize: 14, lineHeight: 1.7,
          }}
        >
          <p>
            The Learn to Trade feature and all content provided by Wealth Financial Systems LLC
            ("Wealth Financial Systems") are for <strong style={{ color: '#f9fafb' }}>informational and educational
            purposes only</strong>. The Learn to Trade feature is not, nor is it intended to be, trading
            or investment advice or a recommendation that any security, futures contract, digital
            asset, other product, transaction, or investment strategy is suitable for any person.
          </p>

          <p style={{ marginTop: '1rem' }}>
            Trading securities, futures products, and options involves risk and may result in a
            loss greater than the original amount invested. Wealth Financial Systems, through its content,
            financial tools, Learn to Trade feature, or otherwise, <strong style={{ color: '#f9fafb' }}>does
            not provide investment or financial advice or make investment recommendations</strong>.
          </p>

          <p style={{ marginTop: '1rem' }}>
            Wealth Financial Systems is not a licensed financial advisor, registered investment advisor, or a
            registered broker-dealer. Wealth Financial Systems is not in the business of transacting securities
            trades, nor does it direct client accounts or give trading advice tailored to any
            particular client's situation. Nothing contained in the Learn to Trade feature
            constitutes a solicitation, recommendation, promotion, or endorsement of any particular
            security, other investment product, transaction, or investment.
          </p>

          <p style={{ marginTop: '1rem' }}>
            The Learn to Trade feature does not take into account your investment objectives,
            financial situation, or needs. <strong style={{ color: '#f9fafb' }}>You are solely responsible
            for making your own investment and trading decisions</strong>, and for evaluating relevant
            risk factors, including your investment experience, objectives, financial situation,
            and risk tolerance to determine if a trade suits your financial needs prior to trading.
          </p>

          <p style={{ marginTop: '1rem' }}>
            The trade examples shown are historical records of completed trades. If you choose to
            place similar trades, your results will differ due to timing, account size, buying
            power, commissions, and market conditions. <strong style={{ color: '#f9fafb' }}>Past
            performance is not indicative of future results.</strong>
          </p>

          <p style={{ marginTop: '1rem' }}>
            Employees, contractors, owners, and others connected with Wealth Financial Systems LLC may own
            positions in the equities, options, securities, and/or futures positions shown
            in the Learn to Trade feature. The trade examples reflect real positions that were
            held by Wealth Financial Systems or its educators.
          </p>

          <p style={{ marginTop: '1rem' }}>
            By enabling the Learn to Trade feature, you acknowledge that you have read and agree to
            the full <a href="/terms#trade-with-me" target="_blank" rel="noopener noreferrer"
            style={{ color: '#60a5fa' }}>Terms and Conditions (Section 19)</a> governing this feature.
          </p>
        </div>

        {/* Footer */}
        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #374151' }}>
          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
            marginBottom: '1rem', opacity: scrolledToBottom ? 1 : 0.4,
          }}>
            <input
              type="checkbox"
              checked={checked}
              disabled={!scrolledToBottom}
              onChange={e => setChecked(e.target.checked)}
              style={{ marginTop: 3, width: 18, height: 18, accentColor: '#6d28d9' }}
            />
            <span style={{ color: '#f9fafb', fontSize: 13, lineHeight: 1.5 }}>
              I acknowledge that the trade examples shown are for educational purposes only and do not
              constitute investment advice. I understand that Wealth Financial Systems LLC is not a financial advisor.
              I am solely responsible for any trading decisions I make.
            </span>
          </label>

          {!scrolledToBottom && (
            <p style={{ color: '#6b7280', fontSize: 11, marginBottom: '0.75rem', textAlign: 'center' }}>
              Scroll to the bottom to enable the checkbox.
            </p>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button
              onClick={onDecline}
              style={{
                padding: '0.6rem 1.5rem', borderRadius: 8, border: '1px solid #374151',
                background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: 14,
              }}
            >
              Decline
            </button>
            <button
              onClick={onAccept}
              disabled={!checked}
              style={{
                padding: '0.6rem 1.5rem', borderRadius: 8, border: 'none',
                background: checked ? '#6d28d9' : '#374151',
                color: checked ? '#f9fafb' : '#6b7280',
                cursor: checked ? 'pointer' : 'not-allowed',
                fontSize: 14, fontWeight: 600,
              }}
            >
              I Agree
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
